/**
 * The live-queue WebSocket client — docs/spec/03, api:docs/spec/01-contracts §9.
 *
 * **The protocol is ratified, not proposed** (Q8 closed, Marc 2026-07-29): the api
 * built `GET /ws` to *this client's* 03 §2 design. Every shape below was then
 * verified against the running staging endpoint on 2026-07-29 — handshake,
 * `auth.ok` latency, and the 25 s ping cadence are observed behaviour, not
 * transcribed documentation.
 *
 * ## The rule that shapes this file
 *
 * **WebSockets are an enhancement, never a requirement** (03 §1). Every failure
 * path here ends in "the app keeps working over HTTP": a socket that cannot open,
 * an auth frame that is rejected, a server that vanishes — none of them may
 * surface an error to the operator or block a screen. The only thing a dead
 * socket changes is that the queue falls back to polling.
 *
 * ## Handshake (api §9)
 *
 * 1. open → client sends `{"type":"auth","token":"<JWT>"}` within a **5 s** grace
 * 2. server replies `{"type":"auth.ok"}`, or an `error` frame then close **1008**
 * 3. client sends `{"type":"subscribe","topics":[…]}` — `queue` for everyone,
 *    `qa` **admin-only** (the server authorizes; we simply never ask when we are
 *    not an admin, so a rejection is a bug rather than routine traffic)
 * 4. server pings every **25 s**; we reply `pong`. Silence for **60 s** closes.
 *
 * The token goes in the first *frame*, never in the URL: a `?access_token=` lands
 * in proxy and access logs, which is exactly the leak 06 §5 forbids.
 */
import { useAuthStore } from './auth'
import type { JobSummary } from './jobs'
import { refreshSessionOnce } from './session'

/** Topics the server understands (api §9). `qa` is admin-only. */
export type LiveTopic = 'queue' | 'qa'

/** A user shown as present on a job. **PII-free by contract** — id + name only. */
export interface PresenceUser {
  id: string
  name: string | null
  job_id?: string | undefined
}

/**
 * Server → client live events (03 §2).
 *
 * `job.submitted` **carries two possible statuses**, and reading it off the
 * message rather than assuming is load-bearing: a transcriber's submit lands in
 * `transcription_qa_check`, an admin's is auto-approved (02 §3). Assuming
 * "submitted ⇒ in QA" drifts the admin queue's count upward from jobs that were
 * never there.
 */
export type LiveEvent =
  | { type: 'job.created'; job: JobSummary }
  /**
   * The four transition events.
   *
   * `job` is the **complete JobLiteSchema row** after the transition — the api
   * added it 2026-07-29 (10 §Q15) built from each handler's in-hand view, so it
   * costs no extra query. Optional in this type because it is absent on any
   * deployment predating that change, and the scalar fields were deliberately
   * kept alongside it so nothing already reading them breaks.
   *
   * Prefer `job` when present: it carries `time_updated`, `completed_at`,
   * `parcel_code` and the sender block, which the scalars do not, so a patched
   * row stops going stale in every column except status.
   */
  | {
      type: 'job.claimed'
      job_id: string
      assignee_id: string | null
      assignee_name: string | null
      status: string
      job?: JobSummary | undefined
    }
  | { type: 'job.updated'; job_id: string; status: string; job?: JobSummary | undefined }
  | { type: 'job.submitted'; job_id: string; status: string; job?: JobSummary | undefined }
  | { type: 'job.reviewed'; job_id: string; status: string; job?: JobSummary | undefined }
  | { type: 'counts'; open: number; in_progress: number; qa_check: number }
  /**
   * `job_id` is the job this frame describes.
   *
   * **Optional because the api does not send it yet** (10 §Q14, verified against
   * staging 2026-07-29 — the field is only on each user). Parsed now so the
   * api's change lands as a no-op here: with it, every frame is attributable,
   * including the `users: []` one that clears the last occupant and today names
   * no job at all.
   */
  | { type: 'presence'; job_id?: string | undefined; users: PresenceUser[] }

/** Connection states surfaced to the UI (03 §5) — drives the polling fallback. */
export type LiveStatus = 'idle' | 'connecting' | 'live' | 'reconnecting' | 'stopped'

/** The slice of `WebSocket` this client uses, so tests can supply a double. */
export interface SocketLike {
  send(data: string): void
  close(code?: number, reason?: string): void
  onopen: ((this: unknown, ev: unknown) => unknown) | null
  onmessage: ((this: unknown, ev: { data: unknown }) => unknown) | null
  onerror: ((this: unknown, ev: unknown) => unknown) | null
  onclose: ((this: unknown, ev: { code: number; reason: string }) => unknown) | null
}

export interface LiveConnectionOptions {
  url: string
  topics: readonly LiveTopic[]
  onEvent: (event: LiveEvent) => void
  onStatus?: ((status: LiveStatus) => void) | undefined
  /** Injected in tests; defaults to the real global. */
  socketFactory?: ((url: string) => SocketLike) | undefined
}

export interface LiveConnection {
  /** Close for good. Idempotent, and never schedules another reconnect. */
  stop(): void
  status(): LiveStatus
  /** Present-on-a-job mutation (03 §2). No-ops while the socket is not live. */
  enterJob(jobId: string): void
  leaveJob(jobId: string): void
}

/** The server's auth grace is 5 s (api §9); fail our own attempt just inside it. */
const AUTH_TIMEOUT_MS = 4_500

/**
 * Reconnect backoff (03 §5). Jittered so a server restart does not bring every
 * operator's browser back in the same millisecond — a thundering herd against
 * the api we just watched recover.
 */
const BACKOFF_BASE_MS = 1_000
const BACKOFF_CAP_MS = 30_000

function backoffDelay(attempt: number, random: () => number): number {
  const exponential = Math.min(BACKOFF_CAP_MS, BACKOFF_BASE_MS * 2 ** attempt)
  // Full jitter: uniform in [0, exponential]. Spreads reconnects the most for a
  // given cap, which is the point of the delay in the first place.
  return Math.floor(random() * exponential)
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

function readString(source: Record<string, unknown>, key: string): string | null {
  const value = source[key]
  return typeof value === 'string' ? value : null
}

function readNumber(source: Record<string, unknown>, key: string): number | null {
  const value = source[key]
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

function parsePresenceUsers(value: unknown): PresenceUser[] {
  if (!Array.isArray(value)) return []
  const users: PresenceUser[] = []
  for (const entry of value) {
    const record = asRecord(entry)
    if (record === null) continue
    const id = readString(record, 'id')
    if (id === null) continue
    const jobId = readString(record, 'job_id')
    users.push({
      id,
      name: readString(record, 'name'),
      ...(jobId === null ? {} : { job_id: jobId }),
    })
  }
  return users
}

/**
 * The optional post-transition row (10 §Q15). Requires an `id` to be usable at
 * all — a row without one cannot be matched to anything, so it is dropped rather
 * than half-applied, and the scalar fields still carry the change.
 */
function parseJobRow(value: unknown): JobSummary | null {
  const row = asRecord(value)
  if (row === null || readString(row, 'id') === null) return null
  return row as unknown as JobSummary
}

/**
 * Parse one server frame into a `LiveEvent`, or `null` for anything we do not
 * handle.
 *
 * **Unknown types are ignored, not errors** — the server does the same with our
 * frames, and it is what lets the api add an event type without breaking every
 * deployed client. A malformed frame is likewise dropped: one bad message must
 * never take down a connection whose only job is to deliver hints.
 */
export function parseLiveEvent(raw: unknown): LiveEvent | null {
  if (typeof raw !== 'string') return null
  let decoded: unknown
  try {
    decoded = JSON.parse(raw)
  } catch {
    return null
  }
  const message = asRecord(decoded)
  if (message === null) return null

  switch (message['type']) {
    case 'job.created': {
      const job = asRecord(message['job'])
      // A `job.created` without its row is useless: the whole point is to insert
      // without a refetch. Dropping it lets the polling fallback fill the gap.
      if (job === null || readString(job, 'id') === null) return null
      return { type: 'job.created', job: job as unknown as JobSummary }
    }
    case 'job.claimed': {
      const jobId = readString(message, 'job_id')
      if (jobId === null) return null
      const row = parseJobRow(message['job'])
      return {
        type: 'job.claimed',
        job_id: jobId,
        assignee_id: readString(message, 'assignee_id'),
        assignee_name: readString(message, 'assignee_name'),
        status: readString(message, 'status') ?? 'transcription_in_progress',
        ...(row === null ? {} : { job: row }),
      }
    }
    case 'job.updated':
    case 'job.submitted':
    case 'job.reviewed': {
      const jobId = readString(message, 'job_id')
      const status = readString(message, 'status')
      // No status means we cannot patch anything truthfully — and guessing one
      // is how a queue count drifts (see the `job.submitted` note above).
      if (jobId === null || status === null) return null
      const row = parseJobRow(message['job'])
      return {
        type: message['type'],
        job_id: jobId,
        status,
        ...(row === null ? {} : { job: row }),
      }
    }
    case 'counts': {
      const open = readNumber(message, 'open')
      const inProgress = readNumber(message, 'in_progress')
      const qaCheck = readNumber(message, 'qa_check')
      if (open === null || inProgress === null || qaCheck === null) return null
      return { type: 'counts', open, in_progress: inProgress, qa_check: qaCheck }
    }
    case 'presence': {
      // Forward-compatible: read the top-level `job_id` when the api starts
      // sending it (10 §Q14), ignore its absence until then.
      const jobId = readString(message, 'job_id')
      return {
        type: 'presence',
        ...(jobId === null ? {} : { job_id: jobId }),
        users: parsePresenceUsers(message['users']),
      }
    }
    default:
      return null
  }
}

/** Close code the server uses for a rejected/expired token (api §9). */
export const CLOSE_AUTH_FAILED = 1008

function defaultSocketFactory(url: string): SocketLike {
  return new WebSocket(url) as unknown as SocketLike
}

/**
 * Open and maintain the live connection.
 *
 * Returns immediately; everything happens on callbacks. The caller gets `stop()`
 * and a status, never the socket — HARD RULE 2 keeps the transport in this file.
 */
export function createLiveConnection(options: LiveConnectionOptions): LiveConnection {
  const factory = options.socketFactory ?? defaultSocketFactory
  const random = Math.random

  let socket: SocketLike | null = null
  let state: LiveStatus = 'idle'
  let attempt = 0
  let stopped = false
  let authTimer: ReturnType<typeof setTimeout> | null = null
  let retryTimer: ReturnType<typeof setTimeout> | null = null
  /**
   * The socket **after** a successful handshake — non-null exactly when we are
   * authenticated, which is also exactly when it is legal to send. Modelling it
   * as one reference rather than a `socket` + `authenticated` pair removes the
   * impossible "authenticated but no socket" state instead of guarding it.
   */
  let live: SocketLike | null = null
  /**
   * 06 R1, applied to the socket: a token rejected on connect earns **one**
   * refresh-and-retry. Without this latch a stale token reconnects forever,
   * refreshing on every attempt — a refresh storm driven by backoff.
   */
  let refreshedOnce = false
  /** Jobs we have declared presence on, replayed after a reconnect (03 §2). */
  const enteredJobs = new Set<string>()

  function setStatus(next: LiveStatus): void {
    if (state === next) return
    state = next
    options.onStatus?.(next)
  }

  function clearTimer(timer: ReturnType<typeof setTimeout> | null): null {
    if (timer !== null) clearTimeout(timer)
    return null
  }

  function sendOn(target: SocketLike, payload: Record<string, unknown>): void {
    try {
      target.send(JSON.stringify(payload))
    } catch {
      // A send on a socket that died between our check and the call is not an
      // error worth surfacing — the close handler is already on its way.
    }
  }

  function teardownSocket(): void {
    authTimer = clearTimer(authTimer)
    if (socket !== null) {
      socket.onopen = null
      socket.onmessage = null
      socket.onerror = null
      socket.onclose = null
      socket = null
    }
    live = null
  }

  function scheduleReconnect(): void {
    if (stopped) return
    setStatus('reconnecting')
    const delay = backoffDelay(attempt, random)
    attempt += 1
    retryTimer = setTimeout(() => {
      retryTimer = null
      connect()
    }, delay)
  }

  /**
   * The token was rejected. Refresh once (06 R1) and reconnect; if the refresh
   * fails the session is genuinely gone, so stop rather than hammer.
   *
   * Deliberately does NOT route the user to `/login`: the HTTP layer owns the
   * session, and a socket — an enhancement — must never be what logs someone
   * out mid-transcription. Their next real request will handle it properly.
   */
  function handleAuthRejected(): void {
    // No `stopped` guard: every caller is a socket handler, and `stop()`
    // detaches those before closing, so this cannot run after stopping.
    if (refreshedOnce) {
      setStatus('stopped')
      return
    }
    refreshedOnce = true
    // `refreshSessionOnce` is the SAME single-flight promise the HTTP client
    // uses (06 R1). Calling it — rather than refreshing independently — is what
    // keeps a socket reconnect and an in-flight 401 retry from becoming two
    // refreshes racing each other.
    void refreshSessionOnce()
      .then((ok) => {
        if (!ok) {
          setStatus('stopped')
          return
        }
        attempt = 0
        scheduleReconnect()
      })
      .catch(() => {
        setStatus('stopped')
      })
  }

  function handleFrame(target: SocketLike, data: unknown): void {
    if (typeof data !== 'string') return

    // Control frames first: they are the handshake and the heartbeat, and they
    // are not `LiveEvent`s the cache should ever see.
    const control = asRecord(safeParse(data))
    const controlType = control === null ? null : control['type']

    if (controlType === 'auth.ok') {
      authTimer = clearTimer(authTimer)
      live = target
      attempt = 0
      refreshedOnce = false
      setStatus('live')
      sendOn(target, { type: 'subscribe', topics: [...options.topics] })
      // Re-declare presence: the server released this connection's jobs when it
      // dropped, so without the replay a reconnect silently vanishes the user
      // from the job they still have open.
      for (const jobId of enteredJobs) sendOn(target, { type: 'presence.enter', job_id: jobId })
      return
    }

    if (controlType === 'ping') {
      sendOn(target, { type: 'pong' })
      return
    }

    if (controlType === 'error') {
      // Pre-auth, an error frame means the token was refused — close is coming.
      if (live === null) handleAuthRejected()
      return
    }

    const event = parseLiveEvent(data)
    if (event !== null) options.onEvent(event)
  }

  function safeParse(raw: string): unknown {
    try {
      return JSON.parse(raw)
    } catch {
      return null
    }
  }

  function connect(): void {
    // No `stopped` guard: the only deferred caller is the retry timer, which
    // `stop()` clears before this could fire.
    const { accessToken: token } = useAuthStore.getState()
    if (token === null) {
      // Not signed in (or signed out under us). Nothing to authenticate with,
      // and retrying would be a loop with no exit.
      setStatus('stopped')
      return
    }

    setStatus(attempt === 0 ? 'connecting' : 'reconnecting')

    let next: SocketLike
    try {
      next = factory(options.url)
    } catch {
      // A malformed URL throws synchronously. Config validated it (01 §6), so
      // this is unexpected — but it still must not throw into the caller.
      scheduleReconnect()
      return
    }
    socket = next

    next.onopen = () => {
      sendOn(next, { type: 'auth', token })
      // If `auth.ok` never comes we are talking to something that is not the
      // live hub — an ingress misroute answers the upgrade and then says nothing.
      // Reaching this callback *means* the handshake never completed: `auth.ok`
      // and teardown both clear the timer.
      authTimer = setTimeout(() => {
        authTimer = null
        try {
          next.close(1000, 'auth timeout')
        } catch {
          /* already gone */
        }
        teardownSocket()
        scheduleReconnect()
      }, AUTH_TIMEOUT_MS)
    }

    next.onmessage = (event) => {
      handleFrame(next, event.data)
    }

    next.onerror = () => {
      // `error` is always followed by `close`; let close own the reconnect so we
      // never schedule two.
    }

    next.onclose = (event) => {
      const wasAuthenticated = live !== null
      teardownSocket()
      // No `stopped` check here on purpose: `stop()` detaches these handlers
      // before it closes the socket, so this cannot run after stopping. Both
      // paths below re-check `stopped` anyway.
      if (event.code === CLOSE_AUTH_FAILED && !wasAuthenticated) {
        handleAuthRejected()
        return
      }
      scheduleReconnect()
    }
  }

  connect()

  return {
    stop(): void {
      stopped = true
      retryTimer = clearTimer(retryTimer)
      const open = socket
      teardownSocket()
      if (open !== null) {
        try {
          open.close(1000, 'client stopped')
        } catch {
          /* already closed */
        }
      }
      enteredJobs.clear()
      setStatus('stopped')
    },
    status: () => state,
    enterJob(jobId: string): void {
      enteredJobs.add(jobId)
      if (live !== null) sendOn(live, { type: 'presence.enter', job_id: jobId })
    },
    leaveJob(jobId: string): void {
      enteredJobs.delete(jobId)
      if (live !== null) sendOn(live, { type: 'presence.leave', job_id: jobId })
    },
  }
}
